import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, FileText, Calendar, Plus, Minus } from "lucide-react";
import "./AIWorkSummaries.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "AI Work Summaries and Automated Reports | TeamGrid";
const DESCRIPTION =
  "Generate automatic daily and monthly work summaries with AI. Track accomplishments, trends and key insights without manual reporting.";
const CANONICAL = "https://teamgrid.ai/features/ai-work-summaries";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const TEAL = "#00D1C1";
const ICON_BG = "#E0FFFC";
const CARD_BORDER = "#EDF4FF";

const insights = [
  {
    text: "Executed detailed SQL queries on seller7-database for order and advertising data analysis",
    muted: false,
  },
  {
    text: "Reviewed and coordinated order processing for Temu and Amazon marketplaces",
    muted: false,
  },
  {
    text: "Analyzed advertising campaign metrics for Amazon Prime Day scaling",
    muted: true,
  },
  {
    text: "Reviewed Shopify API documentation and Python scripts",
    muted: true,
  },
];

const AIWorkSummaries = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  // FAQ data specific to AI Work Summaries — single Q&A
  const aiWorkSummariesFAQs = [
    {
      question: "What are AI Work Summaries?",
      answer:
        "AI Work Summaries automatically generate easy-to-read summaries of what users worked on, including key tasks, patterns, and improvement suggestions — eliminating manual reporting.",
    },
  ];

  return (
    <div
      className="ai-work-summaries-container"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div
                className="max-w-7xl mx-auto flex flex-col items-center text-center"
                style={{ gap: 20 }}
              >
                <div
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1"
                  style={{
                    background: "rgba(0, 209, 193, 0.06)",
                    borderColor: "rgba(0, 209, 193, 0.15)",
                  }}
                >
                  <Zap
                    className="h-3 w-3 flex-shrink-0"
                    style={{ color: TEAL }}
                  />
                  <span
                    className="font-bold uppercase tracking-wide"
                    style={{
                      fontSize: 12,
                      lineHeight: "16px",
                      letterSpacing: "0.6px",
                      color: TEAL,
                    }}
                  >
                    Smart Reports
                  </span>
                </div>
                <h1
                  className="font-extrabold text-black"
                  style={{
                    fontSize: "clamp(2.5rem, 5vw, 62px)",
                    lineHeight: "clamp(50px, 8vw, 72px)",
                  }}
                >
                  AI Work <span style={{ color: TEAL }}>Summaries</span>
                </h1>
                <p
                  className="mx-auto max-w-[768px] text-center"
                  style={{
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  Auto-generated daily and monthly work reports. Stop asking
                  &quot;what did you work on?&quot;
                  <br />
                  TeamGrid knows.
                </p>
              </div>
            </div>

            {/* MAIN FEATURE SECTION — padding 80px, white bg */}
            <section
              className="flex flex-col items-center justify-center px-6"
              style={{
                paddingTop: 80,
                paddingBottom: 40,
                paddingLeft: 80,
                paddingRight: 80,
                background: "#FFFFFF",
              }}
            >
              <div
                className="mb-6 flex w-full max-w-[1280px] flex-col gap-16 lg:flex-row lg:items-center lg:justify-center"
                style={{ gap: 64 }}
              >
                {/* Left: Feature list — 608px, gap 20px */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-1 flex-col items-start lg:max-w-[608px]"
                  style={{ gap: 20 }}
                >
                  <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
                    Automated Work Documentation
                  </h2>
                  <p
                    style={{
                      fontSize: 17,
                      lineHeight: "24px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Our AI analyzes activity patterns, screenshots, and project
                    context to generate comprehensive summaries of what each
                    team member accomplished.
                  </p>

                  <div
                    className="flex flex-col"
                    style={{ paddingTop: 24, gap: 32 }}
                  >
                    <div className="flex items-start gap-6">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                        style={{
                          background: ICON_BG,
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <FileText className="h-5 w-5" style={{ color: TEAL }} />
                      </div>
                      <div className="flex flex-col" style={{ gap: 7 }}>
                        <h3
                          className="font-bold text-black"
                          style={{ fontSize: 15, lineHeight: "28px" }}
                        >
                          Daily Summaries
                        </h3>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          Get automatic end-of-day reports for every team member
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-6">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                        style={{
                          background: ICON_BG,
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <Calendar className="h-5 w-5" style={{ color: TEAL }} />
                      </div>
                      <div className="flex flex-col" style={{ gap: 7 }}>
                        <h3
                          className="font-bold text-black"
                          style={{ fontSize: 15, lineHeight: "28px" }}
                        >
                          Monthly Reports
                        </h3>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          Comprehensive monthly analysis with trends and
                          insights
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-6">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                        style={{
                          background: ICON_BG,
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <Zap className="h-5 w-5" style={{ color: TEAL }} />
                      </div>
                      <div className="flex flex-col" style={{ gap: 7 }}>
                        <h3
                          className="font-bold text-black"
                          style={{ fontSize: 15, lineHeight: "28px" }}
                        >
                          Key Insights
                        </h3>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          Bullet-point highlights of important accomplishments
                        </p>
                      </div>
                    </div>
                  </div>

                  <div style={{ paddingTop: 24 }}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/book-demo")}
                      className="rounded-xl font-bold text-black w-full sm:w-auto"
                      style={{
                        padding: "12px 32px",
                        background: TEAL,
                        boxShadow: "0px 0px 15px rgba(0, 209, 193, 0.3)",
                        fontSize: 16,
                        lineHeight: "24px",
                      }}
                    >
                      Generate Summary
                    </motion.button>
                  </div>
                </motion.div>

                {/* Right: 2. TOP KEY INSIGHTS card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-1 flex-col lg:max-w-[608px]"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${CARD_BORDER}`,
                    boxShadow: "0px 20px 40px -5px rgba(0, 0, 0, 0.1)",
                    borderRadius: 32,
                  }}
                >
                  <div className="flex flex-col gap-1 p-6">
                    <div className="flex items-center gap-2">
                      <Zap
                        className="h-5 w-5 flex-shrink-0"
                        style={{ color: TEAL }}
                      />
                      <h3
                        className="font-bold text-black"
                        style={{ fontSize: 20, lineHeight: "28px" }}
                      >
                        Top Key Insights
                      </h3>
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        lineHeight: "16px",
                        fontWeight: 500,
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      8 insights found
                    </p>
                  </div>

                  <div className="flex flex-col px-6 pb-6" style={{ gap: 12 }}>
                    {insights.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-4 rounded-xl border p-3"
                        style={{
                          background: "rgba(247, 247, 247, 0.4)",
                          borderColor: CARD_BORDER,
                        }}
                      >
                        <div
                          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border bg-white text-center font-bold"
                          style={{
                            borderColor: CARD_BORDER,
                            fontSize: 12,
                            lineHeight: "16px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          {idx + 1}
                        </div>
                        <span
                          className="flex-1 text-left"
                          style={{
                            fontSize: 14,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - Light Grey Background (full-bleed left/right) */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 px-4 sm:px-6 md:px-8 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold text-gray-900 mb-4 max-w-[900px] mx-auto">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for AI work
                    summaries.
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {aiWorkSummariesFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIWorkSummaries;
